import { create } from 'zustand';
import { Category, Tag, categoryApi, tagApi } from '@/lib/itemApi';
import { Column, columnApi, CreateColumnData, UpdateColumnData } from '@/lib/columnApi';

interface WorkspaceMetadataState {
  // Data
  categories: Category[];
  tags: Tag[];
  columns: Column[];
  
  // Loading states
  categoriesLoading: boolean;
  tagsLoading: boolean;
  columnsLoading: boolean;
  
  // Error states
  categoriesError: string | null;
  tagsError: string | null;
  columnsError: string | null;
  
  // Actions
  fetchCategories: (workspaceId: string) => Promise<void>;
  fetchTags: (workspaceId: string) => Promise<void>;
  fetchColumns: (workspaceId: string, includeHidden?: boolean) => Promise<void>;
  fetchAll: (workspaceId: string) => Promise<void>;
  
  // CRUD operations
  createCategory: (workspaceId: string, data: Partial<Category>) => Promise<Category>;
  updateCategory: (workspaceId: string, id: string, data: Partial<Category>) => Promise<Category>;
  deleteCategory: (workspaceId: string, id: string) => Promise<void>;
  
  createTag: (workspaceId: string, data: Partial<Tag>) => Promise<Tag>;
  updateTag: (workspaceId: string, id: string, data: Partial<Tag>) => Promise<Tag>;
  deleteTag: (workspaceId: string, id: string) => Promise<void>;
  
  createColumn: (workspaceId: string, data: CreateColumnData) => Promise<Column>;
  updateColumn: (workspaceId: string, id: string, data: UpdateColumnData) => Promise<Column>;
  deleteColumn: (workspaceId: string, id: string) => Promise<void>;
  reorderColumns: (workspaceId: string, order: string[]) => Promise<void>;
}

export const useWorkspaceMetadataStore = create<WorkspaceMetadataState>()((set, get) => ({
  // Initial state
  categories: [],
  tags: [],
  columns: [],
  categoriesLoading: false,
  tagsLoading: false,
  columnsLoading: false,
  categoriesError: null,
  tagsError: null,
  columnsError: null,

  // Fetch actions
  fetchCategories: async (workspaceId: string) => {
    set({ categoriesLoading: true, categoriesError: null });
    try {
      const data = await categoryApi.getAll(workspaceId);
      set({ categories: data, categoriesLoading: false });
    } catch (err) {
      set({ categoriesError: 'Failed to fetch categories', categoriesLoading: false });
      console.error('Failed to fetch categories:', err);
    }
  },

  fetchTags: async (workspaceId: string) => {
    set({ tagsLoading: true, tagsError: null });
    try {
      const data = await tagApi.getAll(workspaceId);
      set({ tags: data, tagsLoading: false });
    } catch (err) {
      set({ tagsError: 'Failed to fetch tags', tagsLoading: false });
      console.error('Failed to fetch tags:', err);
    }
  },

  fetchColumns: async (workspaceId: string, includeHidden = false) => {
    set({ columnsLoading: true, columnsError: null });
    try {
      const data = await columnApi.getAll(workspaceId, includeHidden);
      set({ columns: data, columnsLoading: false });
    } catch (err) {
      set({ columnsError: 'Failed to fetch columns', columnsLoading: false });
      console.error('Failed to fetch columns:', err);
    }
  },

  fetchAll: async (workspaceId: string) => {
    await Promise.all([
      get().fetchCategories(workspaceId),
      get().fetchTags(workspaceId),
      get().fetchColumns(workspaceId, true),
    ]);
  },

  // Category CRUD
  createCategory: async (workspaceId: string, data: Partial<Category>) => {
    const category = await categoryApi.create(workspaceId, data);
    set((state) => ({ categories: [...state.categories, category] }));
    return category;
  },

  updateCategory: async (workspaceId: string, id: string, data: Partial<Category>) => {
    const updated = await categoryApi.update(workspaceId, id, data);
    set((state) => ({
      categories: state.categories.map((c) => (c.id === id ? updated : c)),
    }));
    return updated;
  },

  deleteCategory: async (workspaceId: string, id: string) => {
    await categoryApi.delete(workspaceId, id);
    set((state) => ({
      categories: state.categories.filter((c) => c.id !== id),
    }));
  },

  // Tag CRUD
  createTag: async (workspaceId: string, data: Partial<Tag>) => {
    const tag = await tagApi.create(workspaceId, data);
    set((state) => ({ tags: [...state.tags, tag] }));
    return tag;
  },

  updateTag: async (workspaceId: string, id: string, data: Partial<Tag>) => {
    const updated = await tagApi.update(workspaceId, id, data);
    set((state) => ({
      tags: state.tags.map((t) => (t.id === id ? updated : t)),
    }));
    return updated;
  },

  deleteTag: async (workspaceId: string, id: string) => {
    await tagApi.delete(workspaceId, id);
    set((state) => ({
      tags: state.tags.filter((t) => t.id !== id),
    }));
  },

  // Column CRUD
  createColumn: async (workspaceId: string, data: CreateColumnData) => {
    const column = await columnApi.create(workspaceId, data);
    set((state) => ({ columns: [...state.columns, column] }));
    return column;
  },

  updateColumn: async (workspaceId: string, id: string, data: UpdateColumnData) => {
    const updated = await columnApi.update(workspaceId, id, data);
    set((state) => ({
      columns: state.columns.map((c) => (String(c.id) === String(id) ? updated : c)),
    }));
    return updated;
  },

  deleteColumn: async (workspaceId: string, id: string) => {
    await columnApi.delete(workspaceId, id);
    set((state) => ({
      columns: state.columns.filter((c) => c.id !== id),
    }));
  },

  reorderColumns: async (workspaceId: string, order: string[]) => {
    const reordered = await columnApi.reorder(workspaceId, order);
    set({ columns: reordered });
  },
}));
